"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DatabaseDiff = exports.TypeDiff = exports.RelationDiff = exports.EntityDiff = exports.CheckDiff = exports.IndexDiff = exports.AttributeDiff = exports.ExtraDiff = exports.OptValueDiff = exports.ValueDiff = exports.ArrayDiff = void 0;
exports.databaseDiff = databaseDiff;
exports.attributesDiff = attributesDiff;
exports.indexesDiff = indexesDiff;
exports.checksDiff = checksDiff;
const zod_1 = require("zod");
const utils_1 = require("@azimutt/utils");
const database_1 = require("./database");
const databaseUtils_1 = require("./databaseUtils");
// FIXME: Work In Progress
// cf https://github.com/andreyvit/json-diff
const ArrayDiff = (schema, schemaDiff) => zod_1.z.object({
    unchanged: schema.and(zod_1.z.object({ i: zod_1.z.number() })).array(), // both present but same
    updated: schemaDiff.array(), // both present but different
    created: schema.and(zod_1.z.object({ i: zod_1.z.number() })).array(), // after only
    deleted: schema.and(zod_1.z.object({ i: zod_1.z.number() })).array(), // before only
}).strict().describe('ArrayDiff');
exports.ArrayDiff = ArrayDiff;
const ValueDiff = (schema) => zod_1.z.object({ before: schema, after: schema }).strict();
exports.ValueDiff = ValueDiff;
const OptValueDiff = (schema) => zod_1.z.object({ before: schema.optional(), after: schema.optional() }).strict();
exports.OptValueDiff = OptValueDiff;
exports.ExtraDiff = zod_1.z.record(zod_1.z.object({ before: zod_1.z.any(), after: zod_1.z.any() })).describe('ExtraDiff');
exports.AttributeDiff = zod_1.z.object({
    name: database_1.AttributeName,
    i: (0, exports.ValueDiff)(zod_1.z.number()).optional(),
    type: (0, exports.ValueDiff)(database_1.AttributeType).optional(),
    null: (0, exports.OptValueDiff)(zod_1.z.boolean()).optional(),
    // gen: z.boolean().optional(), // false when not specified
    default: (0, exports.OptValueDiff)(database_1.AttributeValue).optional(),
    // attrs: z.lazy(() => Attribute.array().optional()),
    doc: (0, exports.OptValueDiff)(zod_1.z.string()).optional(),
    // stats: AttributeStats.optional(),
    extra: exports.ExtraDiff.optional(),
}).strict().describe('AttributeDiff');
exports.IndexDiff = zod_1.z.object({
    attrs: database_1.AttributePath.array(),
    name: database_1.ConstraintName.optional(), // keep the name even if it didn't change
    i: (0, exports.ValueDiff)(zod_1.z.number()).optional(),
    rename: (0, exports.OptValueDiff)(database_1.ConstraintName).optional(),
    unique: (0, exports.OptValueDiff)(zod_1.z.boolean()).optional(),
    partial: (0, exports.OptValueDiff)(zod_1.z.string()).optional(),
    definition: (0, exports.OptValueDiff)(zod_1.z.string()).optional(),
    doc: (0, exports.OptValueDiff)(zod_1.z.string()).optional(),
    // stats: IndexStats.optional(),
    extra: exports.ExtraDiff.optional(),
}).strict().describe('IndexDiff');
exports.CheckDiff = zod_1.z.object({
    attrs: database_1.AttributePath.array(),
    name: database_1.ConstraintName.optional(), // keep the name even if it didn't change
    i: (0, exports.ValueDiff)(zod_1.z.number()).optional(),
    rename: (0, exports.OptValueDiff)(database_1.ConstraintName).optional(),
    predicate: (0, exports.OptValueDiff)(zod_1.z.string()).optional(),
    doc: (0, exports.OptValueDiff)(zod_1.z.string()).optional(),
    // stats: IndexStats.optional(),
    extra: exports.ExtraDiff.optional(),
}).strict().describe('CheckDiff');
exports.EntityDiff = database_1.Namespace.extend({
    name: database_1.EntityName,
    rename: (0, exports.OptValueDiff)(database_1.EntityName).optional(),
    kind: (0, exports.OptValueDiff)(database_1.EntityKind).optional(),
    def: (0, exports.OptValueDiff)(zod_1.z.string()).optional(),
    attrs: (0, exports.ArrayDiff)(database_1.Attribute, exports.AttributeDiff).optional(),
    pk: (0, exports.OptValueDiff)(database_1.PrimaryKey).optional(),
    indexes: (0, exports.ArrayDiff)(database_1.Index, exports.IndexDiff).optional(),
    checks: (0, exports.ArrayDiff)(database_1.Check, exports.CheckDiff).optional(),
    doc: (0, exports.OptValueDiff)(zod_1.z.string()).optional(),
    // stats: EntityStats.optional(),
    extra: exports.ExtraDiff.optional(),
}).strict().describe('EntityDiff');
exports.RelationDiff = zod_1.z.object({
    src: database_1.EntityRef.extend({ attrs: database_1.AttributePath.array() }),
    ref: database_1.EntityRef.extend({ attrs: database_1.AttributePath.array() }),
    name: database_1.ConstraintName.optional(),
    rename: (0, exports.OptValueDiff)(zod_1.z.string()).optional(),
}).strict().describe('RelationDiff');
exports.TypeDiff = database_1.Namespace.extend({
    name: database_1.TypeName,
    rename: (0, exports.OptValueDiff)(database_1.TypeName).optional(),
    alias: (0, exports.OptValueDiff)(zod_1.z.string()).optional(),
    values: (0, exports.OptValueDiff)(zod_1.z.string().array()).optional(),
    attrs: (0, exports.ArrayDiff)(database_1.Attribute, exports.AttributeDiff).optional(),
    definition: (0, exports.OptValueDiff)(zod_1.z.string()).optional(),
    doc: (0, exports.OptValueDiff)(zod_1.z.string()).optional(),
    extra: exports.ExtraDiff.optional(),
}).strict().describe('TypeDiff');
exports.DatabaseDiff = zod_1.z.object({
    entities: (0, exports.ArrayDiff)(database_1.Entity, exports.EntityDiff).optional(),
    relations: (0, exports.ArrayDiff)(database_1.Relation, exports.RelationDiff).optional(),
    types: (0, exports.ArrayDiff)(database_1.Type, exports.TypeDiff).optional(),
}).strict().describe('DatabaseDiff');
function databaseDiff(before, after) {
    const entities = guessRenames(arrayDiffBy(before.entities || [], after.entities || [], databaseUtils_1.entityToId, entityDiff), entityDiff);
    const relations = arrayDiffBy(before.relations || [], after.relations || [], databaseUtils_1.relationToId, relationDiff);
    const types = guessRenames(arrayDiffBy(before.types || [], after.types || [], databaseUtils_1.typeToId, typeDiff), typeDiff);
    return (0, utils_1.removeEmpty)({ entities, relations, types });
}
function entityDiff(before, after) {
    const rename = before.name === after.name ? undefined : valueDiff(before.name, after.name);
    const kind = before.kind === undefined && after.kind === undefined ? undefined : valueDiff(before.kind, after.kind);
    const def = before.def === after.def ? undefined : valueDiff(before.def, after.def);
    const attrs = attributesDiff(before.attrs || [], after.attrs || []);
    const pk = (0, utils_1.equalDeep)(before.pk, after.pk) ? undefined : valueDiff(before.pk, after.pk);
    const indexes = indexesDiff(before.indexes || [], after.indexes || []);
    const checks = checksDiff(before.checks || [], after.checks || []);
    const doc = before.doc === after.doc ? undefined : valueDiff(before.doc, after.doc);
    const extra = extraDiff(before.extra || {}, after.extra || {});
    if ([rename, kind, def, attrs, pk, indexes, checks, doc, extra].every(v => v === undefined))
        return undefined;
    return (0, utils_1.removeUndefined)({ ...(0, databaseUtils_1.typeToNamespace)(after), name: after.name, rename, kind, def, attrs, pk, indexes, checks, doc, extra });
}
function relationDiff(before, after) {
    const rename = before.name === after.name ? undefined : valueDiff(before.name, after.name);
    const doc = before.doc === after.doc ? undefined : valueDiff(before.doc, after.doc);
    const extra = extraDiff(before.extra || {}, after.extra || {});
    if ([rename, doc, extra].every(v => v === undefined))
        return undefined;
    return (0, utils_1.removeUndefined)({ src: (0, databaseUtils_1.relationLinkToAttributeRef)(after.src), ref: (0, databaseUtils_1.relationLinkToAttributeRef)(after.ref), name: after.name, rename, doc, extra });
}
function typeDiff(before, after) {
    const rename = before.name === after.name ? undefined : valueDiff(before.name, after.name);
    const alias = before.alias === after.alias ? undefined : valueDiff(before.alias, after.alias);
    const values = (0, utils_1.arraySame)(before.values || [], after.values || [], (a, b) => a === b) ? undefined : valueDiff(before.values, after.values);
    const attrs = attributesDiff(before.attrs || [], after.attrs || []);
    const definition = before.definition === after.definition ? undefined : valueDiff(before.definition, after.definition);
    const doc = before.doc === after.doc ? undefined : valueDiff(before.doc, after.doc);
    const extra = extraDiff(before.extra || {}, after.extra || {});
    if ([rename, alias, values, attrs, definition, doc, extra].every(v => v === undefined))
        return undefined;
    return (0, utils_1.removeUndefined)({ ...(0, databaseUtils_1.typeToNamespace)(after), name: after.name, rename, alias, values, attrs, definition, doc, extra });
}
function attributesDiff(before, after) {
    return arrayDiffBy(before, after, a => a.name, attributeDiff);
}
function attributeDiff(before, after) {
    const i = before.i === after.i ? undefined : valueDiff(before.i, after.i);
    const type = before.type === after.type ? undefined : valueDiff(before.type, after.type);
    const nullable = before.null === after.null ? undefined : valueDiff(before.null, after.null);
    const defaultValue = before.default === after.default ? undefined : valueDiff(before.default, after.default);
    const doc = before.doc === after.doc ? undefined : valueDiff(before.doc, after.doc);
    const extra = extraDiff(before.extra || {}, after.extra || {});
    if ([i, type, nullable, defaultValue, doc, extra].every(v => v === undefined))
        return undefined;
    return (0, utils_1.removeUndefined)({ ...(0, databaseUtils_1.typeToNamespace)(before), name: before.name, i, type, null: nullable, default: defaultValue, doc, extra });
}
function indexesDiff(before, after) {
    return guessRenames(arrayDiffBy(before, after, i => i.name || i.attrs.map(databaseUtils_1.attributePathToId).join(','), indexDiff), indexDiff);
}
function indexDiff(before, after) {
    const i = before.i === after.i ? undefined : valueDiff(before.i, after.i);
    const rename = before.name === after.name ? undefined : valueDiff(before.name, after.name);
    const unique = before.unique === after.unique ? undefined : valueDiff(before.unique, after.unique);
    const partial = before.partial === after.partial ? undefined : valueDiff(before.partial, after.partial);
    const definition = before.definition === after.definition ? undefined : valueDiff(before.definition, after.definition);
    const doc = before.doc === after.doc ? undefined : valueDiff(before.doc, after.doc);
    const extra = extraDiff(before.extra || {}, after.extra || {});
    if ([i, rename, unique, partial, definition, doc, extra].every(v => v === undefined))
        return undefined;
    return (0, utils_1.removeUndefined)({ attrs: after.attrs, name: after.name, i, rename, unique, partial, definition, doc, extra });
}
function checksDiff(before, after) {
    return guessRenames(arrayDiffBy(before, after, c => c.name || c.attrs.map(databaseUtils_1.attributePathToId).join(','), checkDiff), checkDiff);
}
function checkDiff(before, after) {
    const i = before.i === after.i ? undefined : valueDiff(before.i, after.i);
    const rename = before.name === after.name ? undefined : valueDiff(before.name, after.name);
    const predicate = before.predicate === after.predicate ? undefined : valueDiff(before.predicate, after.predicate);
    const doc = before.doc === after.doc ? undefined : valueDiff(before.doc, after.doc);
    const extra = extraDiff(before.extra || {}, after.extra || {});
    if ([i, rename, predicate, doc, extra].every(v => v === undefined))
        return undefined;
    return (0, utils_1.removeUndefined)({ attrs: after.attrs, name: after.name, i, rename, predicate, doc, extra });
}
function extraDiff(before, after) {
    const res = {};
    Object.entries(before).forEach(([key, value]) => {
        if (!(0, utils_1.anySame)(value, after[key])) {
            res[key] = { before: value, after: after[key] };
        }
    });
    Object.entries(after).forEach(([key, value]) => {
        if (!(key in before)) {
            res[key] = { before: undefined, after: value };
        }
    });
    return Object.keys(res).length === 0 ? undefined : res;
}
// generic functions
function arrayDiffBy(leftArr, rightArr, getKey, makeDiff) {
    const { left: deleted, right: created, both: common } = (0, utils_1.diffBy)(leftArr.map((v, i) => Object.assign({ i }, v)), rightArr.map((v, i) => Object.assign({ i }, v)), getKey);
    const updated = [];
    const unchanged = [];
    common.forEach(({ left, right }) => {
        const res = makeDiff(left, right);
        res === undefined ? unchanged.push(left) : updated.push(res);
    });
    return unchanged.length > 0 || updated.length > 0 || created.length > 0 || deleted.length > 0 ? (0, utils_1.removeEmpty)({ unchanged, updated, created, deleted }) : undefined;
}
function valueDiff(before, after) {
    return { before, after };
}
function guessRenames(arr, makeDiff) {
    const { unchanged, updated, created, deleted } = arr || {};
    const newUnchanged = unchanged || [], newUpdated = updated || [], newDeleted = deleted || [];
    const newCreated = (created || []).filter(afterValue => {
        const { name, ...after } = afterValue;
        const sameDeleted = newDeleted.findIndex(({ name, ...d }) => (0, utils_1.objectSame)(after, d));
        if (sameDeleted !== -1) {
            const diff = makeDiff(newDeleted[sameDeleted], afterValue);
            diff && newUpdated.push(diff);
            newDeleted.splice(sameDeleted, 1);
            return false;
        }
        else {
            return true;
        }
    });
    return newUnchanged.length > 0 || newUpdated.length > 0 || newCreated.length > 0 || newDeleted.length > 0 ? (0, utils_1.removeEmpty)({ unchanged: newUnchanged, updated: newUpdated, created: newCreated, deleted: newDeleted }) : undefined;
}
//# sourceMappingURL=databaseDiff.js.map